import type { OAuthFinalizedRecord } from "@app/types/oauth/client/setup";
import {
  onOAuthFinalized,
  readOAuthFinalized,
} from "@app/types/oauth/client/setup";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import { useCallback, useEffect, useRef, useState } from "react";

// A sign-in left this long is abandoned, not still in progress.
const pendingSignInMaxAgeMs = 15 * 60 * 1000;

function readStartedAtMs(storageKey: string): number | null {
  try {
    const startedAtMs = Number(window.sessionStorage.getItem(storageKey));
    return startedAtMs > 0 && Date.now() - startedAtMs < pendingSignInMaxAgeMs
      ? startedAtMs
      : null;
  } catch {
    return null;
  }
}

interface UsePendingSignInParams {
  // One per card, e.g. the blocked action id.
  key: string;
  provider: string | null;
  // False until the card has what it needs to finish, e.g. loaded servers.
  ready: boolean;
  onFinalized: (connectionId: Result<string, Error>) => void;
}

// Lets a card pick its sign-in back up after the page reloads mid sign-in.
// `start` marks the attempt in sessionStorage (it survives a reload, per tab);
// on mount, a marked card waits for the sign-in window's outcome. Exactly one
// finisher wins `claim`, so a sign-in never completes twice.
export function usePendingSignIn({
  key,
  provider,
  ready,
  onFinalized,
}: UsePendingSignInParams) {
  const storageKey = `memoair_pending_sign_in:${key}`;
  const [resumeFromMs, setResumeFromMs] = useState(() =>
    readStartedAtMs(storageKey)
  );
  const onFinalizedRef = useRef(onFinalized);
  onFinalizedRef.current = onFinalized;

  const start = useCallback(() => {
    try {
      window.sessionStorage.setItem(storageKey, String(Date.now()));
    } catch {
      // Storage blocked: the sign-in still works, it just can't survive a reload.
    }
  }, [storageKey]);

  const claim = useCallback(() => {
    setResumeFromMs(null);
    try {
      const wasPending = window.sessionStorage.getItem(storageKey) !== null;
      window.sessionStorage.removeItem(storageKey);
      return wasPending;
    } catch {
      return true;
    }
  }, [storageKey]);

  useEffect(() => {
    if (resumeFromMs === null || !ready || !provider) {
      return;
    }
    const handle = (record: OAuthFinalizedRecord) => {
      if (
        record.provider !== provider ||
        record.atMs < resumeFromMs ||
        !claim()
      ) {
        return;
      }
      onFinalizedRef.current(
        record.connectionId
          ? new Ok(record.connectionId)
          : new Err(new Error(record.error ?? "The sign-in didn't finish."))
      );
    };
    const existing = readOAuthFinalized();
    if (existing) {
      handle(existing);
    }
    return onOAuthFinalized(handle);
  }, [resumeFromMs, ready, provider, claim]);

  return { isResuming: resumeFromMs !== null, start, claim };
}
